import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCheck, Clock3, Pin, SmilePlus, X } from 'lucide-react';
import { getReactionDetails, summarizeMessageText } from './messengerThreadUtils';

const formatTimestamp = (value) => {
  if (!value) return 'Just now';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Just now';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const buildActorLabel = (actorId, currentUser, otherUser) => {
  if (!actorId) return 'Unknown';
  if (actorId === currentUser?.id) return currentUser?.name || 'You';
  if (actorId === otherUser?.id) return otherUser?.name || 'Contact';
  return 'Participant';
};

export const MessengerMessageInfoSheet = ({
  open,
  message,
  isMine,
  currentUser,
  otherUser,
  onClose,
  dataTestId = 'message-info-sheet',
}) => {
  const metadata = message?.metadata || {};
  const previewText = summarizeMessageText(message) || 'Message';
  const reactionDetails = useMemo(() => getReactionDetails(metadata.reactions, (actorId) => buildActorLabel(actorId, currentUser, otherUser)), [currentUser, metadata.reactions, otherUser]);
  const seenLabel = isMine
    ? (message?.read ? `Seen by ${otherUser?.name || 'them'}` : 'Sent • not seen yet')
    : `Received from ${otherUser?.name || 'them'}`;
  const pinnedLabel = metadata.pinned
    ? `Pinned by ${buildActorLabel(metadata.pinned_by, currentUser, otherUser)}`
    : 'Not pinned in this chat';

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="message-info-sheet"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid={dataTestId}
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 14500, background: 'rgba(19, 17, 28, 0.22)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '18px 14px calc(18px + env(safe-area-inset-bottom, 0px))' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 28 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="premium-card"
            onClick={(event) => event.stopPropagation()}
            style={{ width: '100%', maxWidth: 362, borderRadius: 28, padding: '16px 14px 14px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <p data-testid={`${dataTestId}-title`} style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Message info</p>
              <button type="button" data-testid={`${dataTestId}-close-button`} onClick={onClose} style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid rgba(112, 63, 246, 0.12)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={16} color="#2E274B" />
              </button>
            </div>

            <div data-testid={`${dataTestId}-preview-card`} style={{ borderRadius: 22, background: 'rgba(248,245,255,0.92)', border: '1px solid rgba(112, 63, 246, 0.08)', padding: '12px 13px', marginBottom: 12 }}>
              <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, lineHeight: 1.35, color: 'var(--text)', wordBreak: 'break-word' }}>{previewText}</p>
              <div data-testid={`${dataTestId}-timestamp`} style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', fontFamily: "'Inria Serif', serif", fontSize: 13 }}>
                <Clock3 size={13} /> {formatTimestamp(message?.created_at)}
              </div>
            </div>

            <div data-testid={`${dataTestId}-seen-status`} style={{ borderRadius: 20, background: 'var(--surface)', border: '1px solid rgba(112, 63, 246, 0.08)', padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <CheckCheck size={17} color="#5F4DAA" />
              <div>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)' }}>Seen status</p>
                <p style={{ margin: '1px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{seenLabel}</p>
              </div>
            </div>

            <div data-testid={`${dataTestId}-pinned-status`} style={{ borderRadius: 20, background: 'var(--surface)', border: '1px solid rgba(112, 63, 246, 0.08)', padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Pin size={17} color="#5F4DAA" />
              <div>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)' }}>Pin</p>
                <p style={{ margin: '1px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{pinnedLabel}</p>
              </div>
            </div>

            <div data-testid={`${dataTestId}-reactions-card`} style={{ borderRadius: 20, background: 'var(--surface)', border: '1px solid rgba(112, 63, 246, 0.08)', padding: '11px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: reactionDetails.length ? 10 : 0 }}>
                <SmilePlus size={17} color="#5F4DAA" />
                <div>
                  <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)' }}>Reactions</p>
                  <p style={{ margin: '1px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{reactionDetails.length ? `${reactionDetails.length} reaction${reactionDetails.length > 1 ? 's' : ''}` : 'No reactions yet'}</p>
                </div>
              </div>

              {reactionDetails.length ? (
                <div data-testid={`${dataTestId}-reactions-list`} style={{ display: 'grid', gap: 8 }}>
                  {reactionDetails.map(({ actorId, emoji, label }, index) => (
                    <div key={`${actorId}-${emoji}`} data-testid={`${dataTestId}-reaction-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderRadius: 16, background: 'rgba(248,245,255,0.88)', padding: '9px 10px' }}>
                      <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text)', fontWeight: 700 }}>{label}</span>
                      <span style={{ fontSize: 24, lineHeight: 1 }}>{emoji}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};